import styled from '@emotion/styled';
import type { FC } from 'react';
import userPlaceholder from '~/common/assets/images/user-placeholder.png';
import { Image } from '~/common/components/image';
import { Link } from '~/common/components/link';
import { Text } from '~/common/components/text';
import { FollowListModalType } from '~/common/types/follow';
import officialUserBadgeImage from '~/features/my_page/assets/official_user_badge.svg';
import vipUserBadgeImageBig from '~/features/my_page/assets/vip_badge_big.png';
import vipUserBadgeImageBigWebp from '~/features/my_page/assets/vip_badge_big.webp';
import type { TellerUser } from '~/models/teller-user';
import { Color, Media, FontSize } from '~/utils/styles/enums';

type Props = {
  tellerUser: TellerUser;
  vipBadge: boolean;
  officialBadge: boolean;
  screenLarge: boolean;
  followingCount: number;
  onFollowStatsClick?: (type: FollowListModalType) => void;
};

export const UserHeader: FC<Props> = ({
  tellerUser,
  vipBadge,
  officialBadge,
  screenLarge,
  followingCount,
  onFollowStatsClick,
}) => {
  return (
    <Wrapper>
      <ThumbnailWrapper>
        <Thumbnail
          src={tellerUser.thumbnail || userPlaceholder}
          alt={tellerUser.name}
        />
        {officialBadge ? <OfficialBadge src={officialUserBadgeImage} /> : null}
        {vipBadge ? (
          <VipBadge>
            <source type="image/webp" srcSet={vipUserBadgeImageBigWebp} />
            <Image
              src={vipUserBadgeImageBig}
              htmlWidth="40"
              htmlHeight="24"
              alt={tellerUser.name}
            />
          </VipBadge>
        ) : null}
      </ThumbnailWrapper>
      <UserInfo>
        <Username>{tellerUser.name}</Username>
        <StatsWrapper>
          <Stats>
            <StatsNumber>{tellerUser.searchableStoryCount}</StatsNumber>
            <StatsCategory>ストーリー</StatsCategory>
          </Stats>
          <StatsButton
            onClick={() =>
              onFollowStatsClick
                ? onFollowStatsClick(FollowListModalType.FOLLOWERS)
                : null
            }
          >
            <StatsNumber>
              {tellerUser.followersCount.toLocaleString() || 0}
            </StatsNumber>
            <StatsCategory>フォロワー</StatsCategory>
          </StatsButton>
          <StatsButton
            onClick={() =>
              onFollowStatsClick
                ? onFollowStatsClick(FollowListModalType.FOLLOWINGS)
                : false
            }
          >
            <StatsNumber>{followingCount || 0}</StatsNumber>
            <StatsCategory>フォロー中</StatsCategory>
          </StatsButton>
        </StatsWrapper>
        {!screenLarge ? (
          <ProfileLinkWrapper>
            <ProfileLink href={`/user/${tellerUser.id}`}>
              プロフィールを確認
            </ProfileLink>
          </ProfileLinkWrapper>
        ) : null}
      </UserInfo>
      {screenLarge && (
        <ProfileLinkWrapper>
          <ProfileLink href={`/user/${tellerUser.id}`}>
            プロフィールを確認
          </ProfileLink>
        </ProfileLinkWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
    align-items: end;
  }
`;

const Username = styled(Text)`
  font-size: ${FontSize.SIZE_24};
  font-weight: bold;

  @media ${Media.MEDIUM} {
    font-size: ${FontSize.SIZE_14};
  }
`;

const ThumbnailWrapper = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  margin-right: 16px;
`;

const Thumbnail = styled(Image)`
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 50%;

  @media ${Media.MEDIUM} {
    height: 72px;
    width: 72px;
  }
`;

const VipBadge = styled.picture`
  margin: -10px auto;

  > img {
    width: 40px;
  }
`;

const OfficialBadge = styled(Image)`
  margin: -10px auto;
  width: 24px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StatsWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  max-width: 250px;
`;

const Stats = styled.div`
  text-align: center;
  padding: 10px 10px 10px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatsButton = styled.button`
  display: block;
  text-align: center;
  line-height: 1.7;
  padding: 10px;
  border-radius: 4px;
  color: ${Color.ACCENT_1000};

  &:hover {
    background-color: ${Color.ACCENT_50};
    cursor: pointer;
  }
`;

const StatsNumber = styled(Text)`
  font-size: ${FontSize.SIZE_16};
  font-weight: 600;

  @media ${Media.MEDIUM} {
    font-size: ${FontSize.SIZE_13};
  }
`;

const StatsCategory = styled(Text)`
  font-size: ${FontSize.SIZE_10};
  margin-top: -5px;
`;

const ProfileLinkWrapper = styled.div`
  @media ${Media.LARGE} {
    flex: 0 0 160px;
    margin-top: 40px;
  }

  @media ${Media.MEDIUM} {
    margin-top: 7px;
  }
`;

const ProfileLink = styled(Link)`
  align-items: center;
  line-height: 1.15;
  color: ${Color.ACCENT_1000};
  font-size: ${FontSize.SIZE_12};
  height: 28px;
  padding: 8px 16px;
  background-color: ${Color.STRONG_BG};
  border-radius: 2em;
  width: 160px;

  @media ${Media.MEDIUM} {
    font-size: ${FontSize.SIZE_11};
    padding: 7px 45px;
    height: 24px;
    width: 95%;
    max-width: 260px;
  }
`;
